/**
 * BudgetsDialog — one-shot modal to set every category's budget for a
 * chosen month within a given cost centre.
 *
 * Rows: one line per category with a $ input pre-filled with the current
 * budget (empty/0 clears it).  Save button PUTs each changed row to
 * /api/operations/budgets and refreshes the dashboard.
 */
import { useEffect, useMemo, useState } from "react";
import { Loader2, X } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { money, setBudget } from "@/lib/operations/api";

// "YYYY-MM" → "June 2026"
function longMonth(ym) {
  if (!ym) return "";
  const [y, m] = ym.split("-");
  const d = new Date(Number(y), Number(m) - 1, 1);
  return d.toLocaleString("en-US", { month: "long", year: "numeric" });
}

export default function BudgetsDialog({
  open, onOpenChange,
  costCentre, costCentreLabel,
  months,                 // array of available "YYYY-MM"
  categories,             // active categories [{ key, label }]
  budgets,                // { cat: { "YYYY-MM": amount } } — current values
  spendByCategoryMonthly, // { cat: { "YYYY-MM": amount } } — read-only spend
  defaultMonth,           // "YYYY-MM" | "all"
  onSaved,
}) {
  const [month, setMonth] = useState("");
  const [drafts, setDrafts] = useState({}); // { [catKey]: "123" }
  const [saving, setSaving] = useState(false);

  // Hide the floating Hope AI widget while this modal is open — it sits at
  // z-[70] and would otherwise overlap the Save button on mobile.
  useEffect(() => {
    if (!open) return undefined;
    document.body.dataset.hideHope = "1";
    return () => { delete document.body.dataset.hideHope; };
  }, [open]);

  // Build the month picker list — include the current calendar month as
  // a safety default so the user can still budget even before any data
  // has been uploaded.
  const availableMonths = useMemo(() => {
    const s = new Set(months || []);
    const now = new Date();
    const ym = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
    s.add(ym);
    return Array.from(s).sort();
  }, [months]);

  // On open, snap month to the caller's default (or current calendar month).
  useEffect(() => {
    if (!open) return;
    const now = new Date();
    const currentYm = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
    const initial = defaultMonth && defaultMonth !== "all"
      ? defaultMonth
      : (availableMonths.includes(currentYm) ? currentYm : availableMonths[availableMonths.length - 1] || currentYm);
    setMonth(initial);
  }, [open, defaultMonth, availableMonths]);

  // Reset drafts whenever the month changes.
  useEffect(() => {
    if (!month) return;
    const next = {};
    for (const c of categories) {
      const cur = (budgets?.[c.key] || {})[month];
      next[c.key] = cur ? String(cur) : "";
    }
    setDrafts(next);
  }, [month, categories, budgets]);

  const save = async () => {
    if (!month) return;
    setSaving(true);
    try {
      const changes = [];
      for (const c of categories) {
        const currentBudget = Number((budgets?.[c.key] || {})[month] || 0);
        const nextBudget = Math.max(0, Number(drafts[c.key]) || 0);
        if (currentBudget !== nextBudget) {
          changes.push({ category: c.key, amount: nextBudget });
        }
      }
      if (changes.length === 0) {
        toast.info("No changes to save");
        setSaving(false);
        onOpenChange(false);
        return;
      }
      await Promise.all(
        changes.map((c) =>
          setBudget({
            cost_centre: costCentre,
            category: c.category,
            month,
            amount: c.amount,
          }),
        ),
      );
      toast.success(`Saved ${changes.length} budget${changes.length === 1 ? "" : "s"} for ${longMonth(month)}`);
      onSaved?.();
      onOpenChange(false);
    } catch {
      toast.error("Could not save budgets");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-lg p-0 overflow-hidden flex flex-col top-3 translate-y-0 sm:top-[50%] sm:translate-y-[-50%] max-h-[calc(100dvh-1.5rem)] sm:max-h-[92vh] z-[90]"
        data-testid="ops-budgets-dialog"
      >
        <DialogHeader className="px-5 sm:px-6 pt-5 pb-3 shrink-0 border-b border-slate-100">
          <DialogTitle className="text-brand-navy text-base sm:text-lg leading-tight pr-8">
            Monthly budgets · {costCentreLabel}
          </DialogTitle>
          <p className="text-xs text-slate-500 mt-1">
            Set one budget per category.<br />
            Leave a field blank to clear it.
          </p>
        </DialogHeader>

        {/* Month picker */}
        <div className="px-5 sm:px-6 pt-3 shrink-0">
          <label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
            Month
          </label>
          <div className="mt-1.5 -mx-1 overflow-x-auto">
            <div className="inline-flex items-center gap-1.5 px-1 pb-1">
              {availableMonths.map((m) => (
                <button
                  key={m}
                  type="button"
                  onClick={() => setMonth(m)}
                  className={`shrink-0 h-8 px-3.5 text-xs font-semibold rounded-full transition tabular-nums whitespace-nowrap ${
                    month === m
                      ? "bg-brand-navy text-white shadow-sm"
                      : "text-slate-600 hover:text-brand-navy hover:bg-slate-100 border border-slate-200"
                  }`}
                  data-testid={`ops-budget-month-${m}`}
                >
                  {longMonth(m)}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Category rows — the only scrollable region */}
        <div className="px-5 sm:px-6 pt-3 pb-2 flex-1 min-h-0 overflow-y-auto">
          <div className="rounded-xl border border-slate-100 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 sticky top-0 z-10">
                <tr>
                  <th className="text-left font-semibold px-3 py-2 text-[10px] uppercase tracking-wider">Category</th>
                  <th className="text-right font-semibold px-2 py-2 text-[10px] uppercase tracking-wider hidden sm:table-cell">Spent</th>
                  <th className="text-right font-semibold px-3 py-2 text-[10px] uppercase tracking-wider">Budget</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 bg-white">
                {categories.map((c) => {
                  const spent = Number((spendByCategoryMonthly?.[c.key] || {})[month] || 0);
                  return (
                    <tr key={c.key}>
                      <td className="px-3 py-2 font-semibold text-brand-navy">
                        <div className="flex flex-col">
                          <span>{c.label}</span>
                          <span className="sm:hidden text-[10px] font-normal text-slate-400 tabular-nums">
                            Spent {spent > 0 ? money(spent) : "—"}
                          </span>
                        </div>
                      </td>
                      <td className="px-2 py-2 text-right text-slate-500 tabular-nums text-xs hidden sm:table-cell">
                        {spent > 0 ? money(spent) : "—"}
                      </td>
                      <td className="px-3 py-2 text-right">
                        <div className="relative inline-block">
                          <span className="absolute inset-y-0 left-2 flex items-center text-slate-400 text-xs font-semibold">$</span>
                          <input
                            type="number"
                            inputMode="decimal"
                            min="0"
                            step="0.01"
                            value={drafts[c.key] ?? ""}
                            onChange={(e) => setDrafts((d) => ({ ...d, [c.key]: e.target.value }))}
                            placeholder="0"
                            className="h-9 w-28 sm:w-32 pl-5 pr-2 text-sm font-semibold tabular-nums text-brand-navy rounded-md border border-slate-300 focus:border-brand-navy focus:ring-1 focus:ring-brand-navy outline-none text-right"
                            data-testid={`ops-budget-row-${c.key}`}
                          />
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        <DialogFooter className="px-5 sm:px-6 py-3 border-t border-slate-100 gap-2 shrink-0 bg-white flex-row justify-end">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={saving}
            data-testid="ops-budgets-cancel"
            className="h-10"
          >
            <X className="h-4 w-4 mr-1.5" /> Cancel
          </Button>
          <Button
            type="button"
            onClick={save}
            disabled={saving || !month}
            className="bg-brand-navy hover:bg-brand-navy/90 h-10"
            data-testid="ops-budgets-save"
          >
            {saving && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />}
            Save budgets
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
